/**
 * The card that goes up while the phone is ringing.
 *
 * Built around one question - "who is this?" - because that is the only thing
 * the operator gains by having the CRM answer the phone instead of a handset.
 * The number alone they could read off a desk phone.
 *
 * A shared number resolves to several companies, and all of them are shown. The
 * server deliberately writes none of them onto the call log in that case (see
 * `lib/lookup.ts`), so this list is the only place the ambiguity is visible, and
 * hiding it behind "the first one" would be inventing an answer at the exact
 * moment somebody is about to say hello.
 */
import { Building2, ListTree, Phone, PhoneOff, User, Users } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { formatPhone } from "../../../lib/phone";
import { Avatar } from "../../ui";
import type { PhoneMatch, PhoneMatchKind } from "../../../lib/calls";
import type { IncomingCall as IncomingCallData } from "../CallProvider";
import { useCallAlert } from "./useCallAlert";
import { useRingtone } from "./useRingtone";

/**
 * Each language in its own name.
 *
 * A three-entry map rather than a call into the settings mirror, because this
 * card runs while a phone is ringing and must not depend on settings having
 * been loaded. The list is the server's `NOTICE_LOCALE_NAME`, and a locale that
 * is not in it renders as nothing rather than as a raw tag.
 */
const LOCALE_NAME: Record<string, string> = {
  et: "Eesti keel",
  ru: "Русский",
  en: "English",
};

const KIND_ICON: Record<PhoneMatchKind, typeof Building2> = {
  client: Building2,
  contact: User,
  lead: Users,
  register: Building2,
};

function MatchRow({ match }: { match: PhoneMatch }) {
  const t = useT();
  const Icon = KIND_ICON[match.kind];
  return (
    <li className="crm-inc-match">
      <Icon className="size-3.5 shrink-0 opacity-70" />
      <span className="min-w-0 truncate font-[550]">{match.name}</span>
      <span className="crm-sub ml-auto shrink-0 !mt-0 text-[11px]">
        {t(`calls.incoming.kind.${match.kind}`)}
      </span>
    </li>
  );
}

export function IncomingCall({
  incoming,
  onAccept,
  onReject,
}: {
  incoming: IncomingCallData;
  onAccept: () => void;
  onReject: () => void;
}) {
  const t = useT();
  useRingtone(true);

  // The language in ITS OWN name, not translated: a Russian-speaking operator
  // should find the word they expect, and the autonym is the same string the
  // dial pad and the settings tabs already show.
  const localeName = LOCALE_NAME[incoming.locale] ?? "";

  const best = incoming.matches[0] ?? null;
  const name = best?.name ?? t("calls.incoming.unknownCaller");
  const number = incoming.from ? formatPhone(incoming.from) : t("calls.incoming.withheld");
  // Everything after the first, because the first one is the heading above.
  const others = incoming.matches.slice(1);

  // The same two lines, sent where this card cannot reach: the tab strip, the
  // tab icon and - if the operator switched it on - a system notice. Mounting
  // this component IS the ring, so the alert needs no flag of its own.
  useCallAlert({ caller: name, number });

  return (
    <div className="relative flex min-h-0 flex-1 flex-col px-8 pb-7">
      <div className="-mx-2 flex min-h-0 flex-col items-center overflow-y-auto px-2 pt-[34px]">
        <Avatar name={name} size={78} className={cn("crm-live-av", "is-up")} />
        <span className="crm-live-st mt-[18px]">
          <i />
          {t("calls.incoming.ringing")}
        </span>
        <span className="mt-3 text-[21px] font-bold tracking-[-0.038em]">{name}</span>
        <span className="mt-1.5 text-[12.5px] tabular-nums text-muted-foreground">{number}</span>

        {/* WHAT THE CALLER ASKED FOR, pressed on their own keypad before this
            card went up. Directly under the number and above everything the
            lookup found, because it is the one line here the caller wrote
            themselves - the rest is what this desk already knew about them.

            Rendered only when there is an answer: an empty chip saying nothing
            would read as a menu that failed rather than as a question that was
            never asked. */}
        {incoming.intentDepartment || localeName ? (
          <span className="crm-ctag mt-3">
            <ListTree className="size-2.5" />
            {[incoming.intentDepartment, localeName].filter(Boolean).join(" · ")}
          </span>
        ) : null}

        {others.length > 0 ? (
          <div className="mt-5 w-full">
            <p className="crm-sub !mt-0 mb-1.5 text-[11.5px]">
              {t("calls.incoming.alsoMatches", { n: others.length })}
            </p>
            <ul className="crm-inc-list">
              {others.map((m) => (
                <MatchRow key={`${m.kind}:${m.id}`} match={m} />
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="mt-5 flex flex-none gap-2.5">
        {/* `!mt-0` because `.crm-hangup` carries an 18px top margin for its own
            layout, where it is the only thing in the footer. Here it shares a
            row, and that margin drops it 18px below the button beside it. */}
        <button type="button" className="crm-hangup !mt-0 flex-1" onClick={onReject}>
          <PhoneOff className="size-4" />
          {t("calls.incoming.reject")}
        </button>
        <button type="button" className="crm-answer flex-1" onClick={onAccept} autoFocus>
          <Phone className="size-4" />
          {t("calls.incoming.accept")}
        </button>
      </div>
    </div>
  );
}
